/**
 * Admin Categories Page: list of event categories.
 * Native mobile app style.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageLoader } from "../../components/PageLoader.tsx";
import { Layout } from "../../components/layout/index.ts";
import {
  IconPlus,
  IconAlertTriangle,
  IconTag,
} from "../../components/Icons.tsx";
import { getTelegram, haptic } from "../../lib/telegram.ts";
import { useAdminCategories } from "@/features/admin/hooks";

export function AdminCategoriesPage() {
  const navigate = useNavigate();
  const tg = getTelegram();

  // URQL hook
  const { categories, isLoading, error: categoriesError, refetch } = useAdminCategories();
  const error = categoriesError?.message ?? null;

  // BackButton
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  // Organize categories into hierarchy
  const rootCategories = categories
    .filter((c) => !c.parentId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const getChildren = (parentId: string) =>
    categories
      .filter((c) => c.parentId === parentId)
      .sort((a, b) => a.sortOrder - b.sortOrder);

  const activeCount = categories.filter((c) => c.isActive).length;

  if (isLoading) return <PageLoader message="Загрузка категорий..." />;

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center text-violet-600">
          <IconTag className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">Категории</h1>
          <p className="text-[13px] text-slate-500">
            {activeCount} активных из {categories.length}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic("light");
            navigate("/admin/categories/new");
          }}
          className="w-10 h-10 rounded-xl bg-[var(--color-accent)] text-white flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Создать категорию"
        >
          <IconPlus className="w-5 h-5" />
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="card p-4 xs:p-5 text-center mb-4">
          <IconAlertTriangle className="w-10 h-10 xs:w-12 xs:h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-base xs:text-lg font-bold text-slate-900 mb-2">
            Ошибка загрузки
          </h2>
          <p className="text-xs xs:text-sm text-slate-500 mb-4">{error}</p>
          <button onClick={() => refetch()} className="btn-primary w-full">
            Попробовать снова
          </button>
        </div>
      )}

      {/* Empty state */}
      {!error && categories.length === 0 && (
        <div className="card p-6 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-slate-100 flex items-center justify-center">
            <IconTag className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="text-[15px] font-semibold text-slate-900 mb-2">
            Нет категорий
          </h3>
          <p className="text-[13px] text-slate-500 mb-4">
            Создайте первую категорию для группировки событий
          </p>
          <button
            onClick={() => {
              haptic("light");
              navigate("/admin/categories/new");
            }}
            className="btn-primary"
          >
            Создать категорию
          </button>
        </div>
      )}

      {/* Categories list */}
      {!error && categories.length > 0 && (
        <div className="card overflow-hidden">
          {rootCategories.map((cat, idx) => (
            <div key={cat.id}>
              {/* Root category */}
              <button
                type="button"
                onClick={() => {
                  haptic("light");
                  navigate(`/admin/categories/${cat.id}`);
                }}
                className={`w-full flex items-center gap-3 min-h-[60px] px-4 py-3 active:bg-slate-50 transition-colors text-left ${
                  idx > 0 ? "border-t border-slate-100" : ""
                } ${!cat.isActive ? "opacity-50" : ""}`}
              >
                {/* Color dot */}
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: cat.color ? `${cat.color}20` : "#f1f5f9",
                  }}
                >
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: cat.color || "#94a3b8" }}
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 text-[15px] truncate">
                      {cat.name}
                    </span>
                    {!cat.isActive && (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold text-slate-400 bg-slate-100 rounded">
                        СКРЫТА
                      </span>
                    )}
                  </div>
                  <span className="text-[12px] text-slate-400">
                    {cat.eventCount} событий · {cat.code}
                  </span>
                </div>

                {/* Chevron */}
                <svg
                  className="w-5 h-5 text-slate-300 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              {/* Children */}
              {getChildren(cat.id).map((child) => (
                <button
                  key={child.id}
                  type="button"
                  onClick={() => {
                    haptic("light");
                    navigate(`/admin/categories/${child.id}`);
                  }}
                  className={`w-full flex items-center gap-3 min-h-[52px] pl-8 pr-4 py-2.5 active:bg-slate-50 transition-colors text-left border-t border-slate-100 ${
                    !child.isActive ? "opacity-50" : ""
                  }`}
                >
                  {/* Color dot smaller */}
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: child.color
                        ? `${child.color}15`
                        : "#f8fafc",
                    }}
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: child.color || "#94a3b8" }}
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-700 text-[14px] truncate">
                        {child.name}
                      </span>
                      {!child.isActive && (
                        <span className="px-1 py-0.5 text-[8px] font-bold text-slate-400 bg-slate-100 rounded">
                          СКРЫТА
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-slate-400">
                      {child.eventCount} событий
                    </span>
                  </div>

                  {/* Chevron */}
                  <svg
                    className="w-4 h-4 text-slate-300 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Bottom spacing */}
      <div className="h-6" />
    </Layout>
  );
}
